import { ConductorOneChildActive, Router, ScreenBase } from "@frui.ts/screens";
import LicenseViewModel from "./steps/licenseViewModel";
import * as Sentry from "@sentry/react";
import OutagesViewModel from "viewModels/common/outagesViewModel";
import { SystemComponent } from "models/outage";
import { action, computed, observable, reaction } from "mobx";
import type { ErrorMessage } from "viewModels/common/types";
import LocalizationService from "services/localizationService";
import { getStatusSteps } from "./components/orderStatusSteps";
import ChargeTypeViewModel from "./steps/chargeTypeViewModel";
import ContactViewModel from "./steps/contactViewModel";
import RecapViewModel from "./steps/recapViewModel";
import EnumService from "services/enumService";
import ChargeOrderLicenseItem from "models/chargeOrderLicenseItem";
import ChargeOrderService from "services/chargeOrderService";
import chargeOrderFuelItem from "models/chargeOrderFuelItem";
import ChargeTypesService from "services/chargeTypesService";
import ChargeOrderChargeItem from "models/chargeOrderChargeItem";
import AppRouter from "services/appRouter";
import ChargeOrderContactItem from "models/chargeOrderContactItem";
import FuelTypeViewModel from "./steps/fuelTypeViewModel";
import { scrollToFirstError, scrollToTop, scrollToElement } from "utils/scrollTo";
import { EshopModuleNavigationName } from "../eshopViewModel";
import { PaymentType } from "models/payment";
import { PayGateCommonPaymentStatus } from "models/googlePayInitPaymentResponse";
import ChargeOrder, { ChargeOrderStatus } from "models/chargeOrder";
import PaymentService from "services/payment/paymentService";
import SingleStatusViewModel from "viewModels/common/payment/singleStatusViewModel";
import SingleBankTransferViewModel from "viewModels/common/payment/singleBankTransferViewModel";
import PaymentResult, { PaymentResultFailureOrigin, PaymentResultFailureType } from "models/paymentResult";
import SinglePaymentRetryViewModel from "viewModels/common/payment/singlePaymentRetryViewModel";
import ChargeOrderRepository from "repositories/chargeOrderRepository";
import { PayGatePaymentStatus } from "models/payGatePaymentResponse";
import EventBus, {
  googlePayPayment3DSecureLoaded,
  googlePayPaymentBrowserFingerprintLoaded,
  googlePayPaymentStatusChanged,
  orderIsInvalid,
  paymentCompleteAlert,
  paymentEmailAddressInvalid,
  paymentFailed,
  paymentFuelTypeNotAllowed,
  submitGooglePayPaymentDeferred,
  submitGooglePayPaymentFailure,
  submitGooglePayPaymentSuccess,
} from "services/eventBus";
import {
  NOTIFICATION_SERVICE_CONNECTION_ID_UNDEFINED,
  NOTIFICATION_SERVICE_INIT_FAILURE,
  NOTIFICATION_SERVICE_START_FAILURE,
  NOTIFICATION_SERVICE_STOP_FAILURE,
  OUTAGE_ACTIVATED,
} from "repositories/order/constants";
import { clearSearchParameters } from "utils/browserHistory";
import ChargeOrderResponse from "entities/chargeOrderResponse";
import ChargeTypeResponse from "entities/chargeTypeResponse";

export const OrderNewViewNavigationName = "order";

@Router.registerRoute({
  name: Router.Self,
  route: OrderNewViewNavigationName,
  children: [LicenseViewModel, FuelTypeViewModel, ChargeTypeViewModel, ContactViewModel, RecapViewModel],
})
export default class OrderWizardViewModel extends ConductorOneChildActive<ScreenBase> {
  public payGateOutagesViewModel: OutagesViewModel;
  public eshopOutagesViewModel: OutagesViewModel;
  public licenseViewModel: LicenseViewModel;
  public fuelTypeViewModel: FuelTypeViewModel;
  public chargeTypeViewModel: ChargeTypeViewModel;
  public contactViewModel: ContactViewModel;
  public recapViewModel: RecapViewModel;
  private unsubscriptions: Array<() => void> = [];
  private intervalId?: any;

  readonly IDLE_TIMER_DELAY = 1000 * 150; // Time before countdown tooltip is shown (150 seconds)
  readonly IDLE_TIMER_TIMEOUT = 1000 * 270; // Time when idle modal is shown (270 seconds)
  readonly IDLE_PROMPT_TIMEOUT = 1000 * 30; // Time in modal before form deletion (30 seconds) - Total: 300s
  readonly IDLE_TIMER_DEBOUNCE = 250;

  @observable days?: number;
  @observable chargeId?: string;
  @observable formElement?: HTMLFormElement;
  @observable errorMessage?: ErrorMessage;
  @observable resetChargeOrder?: boolean = false;
  @observable showIdleModal = false;
  @observable counter: number;
  @observable showPaymentLoadingModal = false;
  @observable showGooglePay3DSecureModal = false;
  @observable googlePay3DSecureUrl?: string;
  @observable paymentResultFailureType?: PaymentResultFailureType;
  @observable paymentResultFailureOrigin?: PaymentResultFailureOrigin;
  @observable paymentIsComplete = false;
  @observable proposedPurchaseRequestID?: string;
  statusNavigation = getStatusSteps();

  constructor(
    public localizationService: LocalizationService,
    public chargeOrderService: ChargeOrderService,
    private chargeTypesService: ChargeTypesService,
    private enumService: EnumService,
    private paymentService: PaymentService,
    private licenseViewModelFactory: ReturnType<typeof LicenseViewModel.Factory>,
    private chargeTypeViewModelFactory: ReturnType<typeof ChargeTypeViewModel.Factory>,
    private contactViewModelFactory: ReturnType<typeof ContactViewModel.Factory>,
    private fuelTypeViewModelFactory: ReturnType<typeof FuelTypeViewModel.Factory>,
    private recapViewModelFactory: ReturnType<typeof RecapViewModel.Factory>,
    private outagesViewModelFactory: ReturnType<typeof OutagesViewModel.Factory>,
    private eventBus: EventBus,
    private router: AppRouter,
    private chargeOrderRepository: ChargeOrderRepository
  ) {
    super();
    this.navigationName = OrderNewViewNavigationName;
    this.nameValue = "eshop.title";
    this.payGateOutagesViewModel = this.outagesViewModelFactory(SystemComponent.PAY_GATE);
    this.eshopOutagesViewModel = this.outagesViewModelFactory(SystemComponent.ESHOP);
    this.counter = this.IDLE_PROMPT_TIMEOUT;
    this.initChildren();
  }

  private initChildren() {
    this.children.clear(); // Note, calling this does not remove the underlying view models.
    this.children.push(
      (this.licenseViewModel = this.licenseViewModelFactory(
        new ChargeOrderLicenseItem(
          this.chargeOrderItem?.licensePlate,
          this.chargeOrderItem?._licensePlateConfirmation,
          this.chargeOrderItem?.countryId,
          true
        )
      )),
      (this.fuelTypeViewModel = this.fuelTypeViewModelFactory(new chargeOrderFuelItem(this.chargeOrderItem._fuelTypeLimitation))),
      (this.chargeTypeViewModel = this.chargeTypeViewModelFactory(
        new ChargeOrderChargeItem(this.chargeOrderItem.validity.validSinceDate, this.chargeOrderItem.chargeTypeId)
      )),
      (this.contactViewModel = this.contactViewModelFactory(
        new ChargeOrderContactItem(this.chargeOrder.notificationPreferences)
      )),
      (this.recapViewModel = this.recapViewModelFactory(this.chargeOrder, this.payOrder))
    );
  }

  private updatePrefilledValues() {
    this.licenseViewModel.vehicleInfoViewModel.item.countryId = this.chargeOrderItem.countryId;
    this.licenseViewModel.vehicleInfoViewModel.item.licensePlate = this.chargeOrderItem.licensePlate;
    if (this.licenseViewModel.vehicleInfoViewModel.item instanceof ChargeOrderLicenseItem) {
      this.licenseViewModel.vehicleInfoViewModel.item._licensePlateConfirmation = this.chargeOrderItem.licensePlate;
    }
  }

  protected async onInitialize(): Promise<Promise<any> | void> {
    await this.enumService.startInitialization();
    await this.chargeTypesService.startInitialization();

    return super.onInitialize();
  }

  protected async onActivate() {
    if (
      this.chargeOrderService.chargeOrder.charges.length > 1 ||
      this.chargeOrderService.chargeOrder._origin === "multiple" ||
      this.chargeOrderService.chargeOrder.charges[0].licensePlates.length >= 1
    ) {
      this.resetOrder();
    }
    if (!this.activeChild || !this.chargeOrderService.chargeOrder.charges[0].licensePlate) {
      await this.tryActivateChild(this.licenseViewModel);
    }

    switch (this.chargeOrder._status) {
      case ChargeOrderStatus.Paid:
      case ChargeOrderStatus.PaidBankTransfer:
      case ChargeOrderStatus.Deferred:
        this.resetOrder();
        // UC - User goes come with back button -> disable user to navigate forward
        window.history.pushState({}, window.document.title, window.location.href);
        break;
      case ChargeOrderStatus.Failed:
        this.initChildren();
        this.chargeOrderService.reopenOrder();
        this.router.navigate(LicenseViewModel);
        break;
    }

    if (this.chargeId) {
      await this.chargeOrderService.prefillPremadeOrder({ chargeId: this.chargeId });
      this.updatePrefilledValues();
      this.chargeId = undefined;

      if (this.chargeOrderService.smartRedirectRequiresReset) {
        this.router.navigate(LicenseViewModel);
      } else {
        this.router.navigate(RecapViewModel);
      }
    } else if (this.proposedPurchaseRequestID) {
      await this.chargeOrderService.prefillPremadeOrder({ proposedPurchaseRequestID: this.proposedPurchaseRequestID });
      this.updatePrefilledValues();
      this.proposedPurchaseRequestID = undefined;

      if (this.chargeOrderService.smartRedirectRequiresReset) {
        this.router.navigate(LicenseViewModel);
      } else {
        this.router.navigate(RecapViewModel);
      }
    } else if (this.days) {
      const chargeTypes = (await this.enumService.lazyValues("charge_types")) as ChargeTypeResponse[];
      this.resetOrder();
      this.chargeOrderService.preselectChargeType(this.days, chargeTypes);
      this.days = undefined;
    }

    this.unsubscriptions.push(
      this.eventBus.subscribe(submitGooglePayPaymentFailure, this.googlePayPaymentFailure),
      this.eventBus.subscribe(submitGooglePayPaymentSuccess, this.googlePayPaymentSuccess),
      this.eventBus.subscribe(submitGooglePayPaymentDeferred, this.googlePayPaymentDeferred),
      this.eventBus.subscribe(googlePayPaymentBrowserFingerprintLoaded, this.googlePayPaymentBrowserFingerprintLoaded),
      this.eventBus.subscribe(googlePayPayment3DSecureLoaded, this.googlePayPayment3DSecureLoaded),
      this.eventBus.subscribe(googlePayPaymentStatusChanged, this.googlePayPaymentStatusChanged),
      this.eventBus.subscribe(paymentFailed, this.paymentFailed),
      this.eventBus.subscribe(orderIsInvalid, this.onOrderValidationFailed),
      this.eventBus.subscribe(paymentCompleteAlert, this.onPaymentCompleteAlert),
      this.eventBus.subscribe(paymentFuelTypeNotAllowed, this.onPaymentFuelTypeNotAllowed),
      this.eventBus.subscribe(paymentEmailAddressInvalid, this.onPaymentEmailAddressInvalid),
      reaction(
        () => this.chargeOrderService.isWholeOrderValid,
        isWholeOrderValid => {
          if (isWholeOrderValid && this.errorMessage === "invalidData") {
            this.errorMessage = undefined;
          }
        }
      ),
      reaction(() => this.recapViewModel.chargeOrder._termsAgreement, this.checkTermsAgreementError),
      reaction(
        () => this.activeChild,
        () => {
          if (this.errorMessage === "requestProcessingFailed" && !(this.activeChild instanceof RecapViewModel)) {
            this.errorMessage = undefined;
          }
        }
      )
    );
    this.chargeOrderService.chargeOrder._origin = "single";
    return super.onActivate();
  }

  protected onDeactivate(isClosing: boolean): Promise<void> {
    this.days = undefined;
    this.chargeId = undefined;
    clearSearchParameters();
    this.clearTimeoutInterval();
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }
    return super.onDeactivate(isClosing);
  }

  @computed
  get chargeOrder() {
    return this.chargeOrderService.chargeOrder;
  }

  @computed
  get chargeOrderItem() {
    return this.chargeOrder.charges[0];
  }

  @computed
  get onBackIsDisabled() {
    return this.activeChild?.navigationName === this.licenseViewModel.navigationName;
  }

  @computed
  get navigationStep() {
    return this.statusNavigation.find(status => status.label === this.activeChild?.navigationName)?.number;
  }

  @computed
  get translateCountryWarning() {
    return this.localizationService.translateAttribute("orderItem", "countryWarning");
  }

  @computed
  get showInlineErrorMessage() {
    return this.errorMessage;
  }

  @computed
  get showPaymentErrorMessage() {
    return !!(this.paymentResultFailureType && this.paymentResultFailureOrigin);
  }

  @computed
  get showPaymentIsComplete() {
    return this.paymentIsComplete;
  }

  @computed
  get paymentErrorTitle(): string {
    return this.localizationService.translateGeneral(
      `eshop.payment.status.failure.messages.${this.paymentResultFailureOrigin}.${this.paymentResultFailureType}.title`
    );
  }

  @computed
  get paymentErrorDescription(): string {
    return this.localizationService.translateGeneral(
      `eshop.payment.status.failure.messages.${this.paymentResultFailureOrigin}.${this.paymentResultFailureType}.description`
    );
  }

  @computed
  get showPaymentErrorLink(): boolean {
    return (
      this.paymentResultFailureOrigin == PaymentResultFailureOrigin.Init &&
      this.paymentResultFailureType == PaymentResultFailureType.AlreadyConfirmed
    );
  }

  @action.bound
  navigate(subpath: any, params: any) {
    if (params && params.proposed_purchase_request_id) {
      this.proposedPurchaseRequestID = params.proposed_purchase_request_id;
    }
    const days = params && Number(params.days);
    if (!isNaN(days) && days > 0) {
      this.days = days;
    }

    this.chargeId = params && params.charge_id;

    // There was `return Promise.resolve()` for some reason, which created double history entry by removing
    // the querystring from URL
    return super.navigate(subpath, params);
  }

  @computed
  get navigationParams() {
    return this.days
      ? {
          days: this.days,
          charge_id: this.chargeId,
        }
      : undefined;
  }

  @action.bound
  closeIdleModal() {
    this.showIdleModal = false;
    this.clearTimeoutInterval();
  }

  @action.bound
  onIdleTimout() {
    this.showIdleModal = false;
    this.chargeOrderService.reset();
    window.location.href = this.localizationService.translateGeneral("eshop.idle.timeoutRedirect", undefined, true);
  }

  @action.bound
  closePaymentFailureModal() {
    this.paymentResultFailureType = undefined;
    this.paymentResultFailureOrigin = undefined;

    // Prefill from existing order and navigate to recap if all data is valid
    this.chargeOrderService.reopenOrder();
    this.initChildren();
    this.updatePrefilledValues();

    const orderItem = this.chargeOrderItem;
    const hasAllData =
      orderItem?.licensePlate &&
      orderItem?.countryId &&
      orderItem?._fuelTypeId &&
      orderItem?._chargeTypeId &&
      orderItem?.priceListItemId &&
      this.chargeOrder.emailAddress;

    if (hasAllData && this.chargeOrderService.validateOrder(true)) {
      this.router.navigate(RecapViewModel);
    } else {
      this.router.navigate(LicenseViewModel);
    }
  }

  @action.bound
  closePaymentComplete() {
    this.chargeOrderService.reset();
    localStorage.setItem("orderReloaded", "true");
    this.paymentIsComplete = false;
    window.location.reload();
  }

  @action.bound
  private clearTimeoutInterval() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
  }

  @action.bound
  async handleOnIdle() {
    if (this.showIdleModal || this.showPaymentErrorMessage) return;

    this.counter = this.IDLE_PROMPT_TIMEOUT;
    this.showIdleModal = true;
    this.intervalId = setInterval(async () => {
      this.counter = this.counter - 1000;
      if (this.counter === 0) {
        this.onIdleTimout();
      }
    }, 1000);
  }

  @action.bound
  protected paymentFailed(paymentError: { type: string; payload: { message: string } }) {
    switch (paymentError.payload.message) {
      case NOTIFICATION_SERVICE_INIT_FAILURE:
      case NOTIFICATION_SERVICE_CONNECTION_ID_UNDEFINED:
      case NOTIFICATION_SERVICE_START_FAILURE:
      case NOTIFICATION_SERVICE_STOP_FAILURE:
        this.errorMessage = "websocket";
        Sentry.withScope(scope => {
          scope.setTag("websocket", "paymentFailed");
          const error = new Error(`Notification Service Error: ${paymentError.payload.message}`);
          error.name = "NotificationServiceError";
          Sentry.captureException(error);
        });
        break;

      case OUTAGE_ACTIVATED:
        scrollToTop();
        break;
    }

    this.paymentLoadingModalToggle(false);
  }

  @action.bound
  paymentLoadingModalToggle = (open: boolean) => {
    this.showPaymentLoadingModal = open;
  };

  @action.bound
  protected async googlePayPaymentFailure() {
    this.paymentLoadingModalToggle(false);
    this.chargeOrder._status = ChargeOrderStatus.Failed;
    this.chargeOrderService.persistChargeOrder();
    this.showGooglePay3DSecureModal = false;
    this.googlePay3DSecureUrl = undefined;

    // Check if payment retry is possible
    if (this.chargeOrder.id) {
      try {
        const payGatePayment = await this.chargeOrderRepository.getPayGatePayment(this.chargeOrder.id);
        if (payGatePayment?.permittedActions?.paymentRetry?.isAllowed) {
          await this.router.navigate(SinglePaymentRetryViewModel, { orderId: this.chargeOrder.id });
          scrollToTop();
          return;
        }
        if (payGatePayment?.status === PayGatePaymentStatus.IN_PROGRESS) {
          this.chargeOrder._status = ChargeOrderStatus.Deferred;
          this.chargeOrderService.persistChargeOrder();
          this.router.navigate(SingleStatusViewModel, { orderId: this.chargeOrder.id }, { result: PaymentResult.DEFERRED });
          scrollToTop();
          return;
        }
      } catch {
        // Fall through to existing behavior
      }
    }

    this.errorMessage = "paymentFailure";
    this.initChildren();
    scrollToTop();
  }

  @action.bound
  protected async googlePayPaymentSuccess() {
    this.paymentLoadingModalToggle(false);
    this.chargeOrder._status = ChargeOrderStatus.Paid;
    this.showGooglePay3DSecureModal = false;
    this.router.navigate(SingleStatusViewModel, { orderId: this.chargeOrder.id }, { result: PaymentResult.SUCCESS });
    scrollToTop();

    try {
      await this.chargeOrderService.updateChargeOrderStatus(PaymentResult.SUCCESS, this.chargeOrder);
      this.chargeOrderService.persistChargeOrder();
    } catch {
      // the user is already on the success screen.
    }
  }

  @action.bound
  protected googlePayPaymentDeferred() {
    this.paymentLoadingModalToggle(false);
    this.chargeOrder._status = ChargeOrderStatus.Deferred;
    this.chargeOrderService.persistChargeOrder();
    this.showGooglePay3DSecureModal = false;
    this.router.navigate(SingleStatusViewModel, { orderId: this.chargeOrder.id }, { result: PaymentResult.DEFERRED });
    scrollToTop();
  }

  @action.bound
  protected googlePayPaymentBrowserFingerprintLoaded() {
    return this.paymentService.confirmGooglePayPayment(this.chargeOrder.id);
  }

  @action.bound
  protected googlePayPayment3DSecureLoaded(result: { payload: { url: string } }) {
    this.paymentLoadingModalToggle(false);
    this.showGooglePay3DSecureModal = true;
    this.googlePay3DSecureUrl = result.payload.url;
  }

  @action.bound
  googlePayPaymentStatusChanged(result: { payload: { status: PayGateCommonPaymentStatus } }) {
    if (result.payload.status != PayGateCommonPaymentStatus.IN_PROGRESS) {
      this.paymentService.finalizeGooglePayPayment(result.payload.status);
    }
  }

  @action.bound
  private async payOrder(
    errorMessage: ErrorMessage,
    paymentData?: google.payments.api.PaymentData,
    turnstileToken?: string | null
  ) {
    this.errorMessage = errorMessage;
    if (this.errorMessage) {
      this.paymentLoadingModalToggle(false);
      return;
    }
    this.paymentLoadingModalToggle(true);

    await this.chargeOrderService.payOrder(turnstileToken, true, async (chargeOrder: ChargeOrderResponse) => {
      await this.chargeOrderService.updateOrder(chargeOrder);
      this.chargeOrder.id = chargeOrder.id;
      const order = await this.chargeOrderService.chargeOrder;
      switch (order.paymentType) {
        case PaymentType.PayGate:
          await this.paymentService.payGateRedirect(chargeOrder.id, EshopModuleNavigationName).then(() => {
            this.paymentLoadingModalToggle(false);
          });
          break;
        case PaymentType.Transfer:
          this.paymentLoadingModalToggle(false);
          scrollToTop();
          const orderId = this.chargeOrderService.orderPaidByBankTransfer();
          this.router.navigate(SingleBankTransferViewModel, { orderId });
          break;
        case PaymentType.GooglePay:
          try {
            if (paymentData === undefined) {
              throw Error("payment data not defined");
            }
            await this.paymentService.submitGooglePayment(chargeOrder.id, paymentData);
          } catch (error) {
            Sentry.captureException(error);
            this.googlePayPaymentFailure();
          }
          break;
        default:
          this.paymentLoadingModalToggle(false);
          break;
      }
    });
  }

  @action.bound
  onOrderValidationFailed() {
    if (this.payGateOutagesViewModel.hasAnyActiveOutage && this.chargeOrder.paymentType === undefined) {
      // Scroll to outage message when paygate outage is active
      scrollToTop();
    } else {
      scrollToFirstError(this.formElement);
    }
  }

  @action.bound
  onPaymentCompleteAlert(data: ReturnType<typeof paymentCompleteAlert>) {
    if (data.payload.orderId) {
      this.paymentIsComplete = true;
      scrollToElement(this.formElement);
    }
  }
  @action.bound
  checkTermsAgreementError() {
    if (
      this.recapViewModel.chargeOrder._termsAgreement &&
      this.errorMessage === "invalidData" &&
      this.chargeOrderService.validateOrder(true)
    ) {
      this.errorMessage = undefined;
    }
  }

  @action.bound
  async onPaymentFuelTypeNotAllowed() {
    await this.licenseViewModel.vehicleRegistrationsReaction(false);
    this.licenseViewModel.updateChargeOrderItem();
    this.router.navigate(FuelTypeViewModel);
    this.paymentLoadingModalToggle(false);
  }

  @action.bound
  onPaymentEmailAddressInvalid() {
    this.errorMessage = "requestProcessingFailed";
    this.paymentLoadingModalToggle(false);
    scrollToTop();
  }

  @action.bound
  resetOrder() {
    this.errorMessage = undefined;
    this.chargeOrderService.reset();
    this.initChildren();
    this.router.navigate(LicenseViewModel);
  }

  @action.bound
  initChildrenFromExistingOrder() {
    this.errorMessage = undefined;
    this.paymentResultFailureType = undefined;
    this.paymentResultFailureOrigin = undefined;
    this.initChildren();
    this.updatePrefilledValues();

    // Check if all required data is available and valid for direct navigation to recap
    const orderItem = this.chargeOrderItem;
    const hasAllData =
      orderItem?.licensePlate &&
      orderItem?.countryId &&
      orderItem?._fuelTypeId &&
      orderItem?._chargeTypeId &&
      orderItem?.priceListItemId &&
      this.chargeOrder.emailAddress;

    if (hasAllData && this.chargeOrderService.validateOrder(true)) {
      this.router.navigate(RecapViewModel);
    } else {
      this.router.navigate(LicenseViewModel);
    }
  }
}
